import React, { useState } from "react";
import { Link, useNavigate, Navigate } from "react-router-dom";
import { LogIn, Loader2, ArrowLeft } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import useSeo from "@/hooks/useSeo";

export default function AdminLoginPage() {
  useSeo({ title: "Admin Login", path: "/admin/login", noindex: true });
  const { login, user, loading } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (!loading && user) return <Navigate to="/admin/leads" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await login(email, password);
      navigate("/admin/leads", { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(
        typeof detail === "string"
          ? detail
          : "Anmeldung fehlgeschlagen. Bitte erneut versuchen."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main
      data-testid="admin-login-page"
      className="min-h-screen bg-[#1F2937] text-[#F7F9FB] flex items-center justify-center px-6"
    >
      <div className="w-full max-w-md">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm text-[#F7F9FB]/60 hover:text-[#2E89C4] transition mb-8"
        >
          <ArrowLeft size={16} />
          Zurück zur Startseite
        </Link>

        <div className="rounded-3xl border border-[#F7F9FB]/10 bg-[#F7F9FB]/[0.04] backdrop-blur-sm p-8 lg:p-10">
          <div className="flex items-center gap-2 mb-6">
            <span className="inline-block h-2.5 w-2.5 rounded-full bg-[#2E89C4]" />
            <span className="font-display font-bold text-lg">
              Alex Buchner <span className="text-[#6FB04F]">Health</span>
            </span>
          </div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight mb-2">
            Admin-Bereich
          </h1>
          <p className="text-sm text-[#F7F9FB]/65 mb-8">
            Melde dich an, um eingegangene Anfragen einzusehen.
          </p>

          <form onSubmit={onSubmit} className="space-y-4" noValidate>
            <div>
              <label className="block text-xs font-semibold tracking-wide uppercase text-[#F7F9FB]/55 mb-2">
                E-Mail
              </label>
              <input
                type="email"
                required
                data-testid="admin-login-email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                className="w-full bg-[#1F2937] border border-[#F7F9FB]/15 rounded-xl px-4 py-3.5 text-[15px] text-[#F7F9FB] focus:outline-none focus:border-[#2E89C4] focus:ring-2 focus:ring-[#2E89C4]/20 transition"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold tracking-wide uppercase text-[#F7F9FB]/55 mb-2">
                Passwort
              </label>
              <input
                type="password"
                required
                data-testid="admin-login-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                className="w-full bg-[#1F2937] border border-[#F7F9FB]/15 rounded-xl px-4 py-3.5 text-[15px] text-[#F7F9FB] focus:outline-none focus:border-[#2E89C4] focus:ring-2 focus:ring-[#2E89C4]/20 transition"
              />
            </div>

            {error && (
              <div
                data-testid="admin-login-error"
                className="text-sm text-red-300 bg-red-900/30 border border-red-500/30 rounded-lg px-4 py-3"
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              data-testid="admin-login-submit"
              disabled={submitting}
              className="btn-primary w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full text-sm font-semibold disabled:opacity-70"
            >
              {submitting ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  Anmelden …
                </>
              ) : (
                <>
                  Anmelden
                  <LogIn size={16} />
                </>
              )}
            </button>
          </form>
        </div>
      </div>
    </main>
  );
}
